import { useViewershipPlatformsByDares, } from 'root/src/client/logic/dropEvent/hooks/useViewershipPlatformsByDares';
import { useUserCreatorData } from 'root/src/client/logic/auth/hooks/useUser';
import { useDropEvent, useUserCampaignData } from 'root/src/client/logic/dropEvent/hooks/useDropEvent';
import { calculateMaxDaresByPlatform, } from 'root/src/shared/util/calculateMaxDares';
import { DarePlatform } from 'root/src/shared/@types/platforms';
import { useMaxDaresByPlatforms } from 'root/src/client/logic/dropEvent/hooks/useMaxDaresByPlatforms';
import { formatNumberWithCommas } from 'root/src/shared/util/formatNumberWithCommas';
import { useIsBrand } from 'root/src/client/v2/auth/hooks/useIsBrand';
import { useIsAdmin } from 'root/src/client/v2/auth/hooks/useIsAdmin';
import { useIsAuthenticated } from 'root/src/client/v2/auth/hooks/useIsAuthenticated';
import { isString } from 'remeda';
import { getReadableViewershipPlatform } from 'root/src/shared/platforms/helpers/getReadablePlatform';
const VIEWERSHIP_REQUIRED_OFFSET = 1;
// `fallbackDrop` follows the same pattern as useMaxDaresByPlatforms/useViewershipPlatformsByDares: the
// redesign drop page loads the drop over tRPC and never fills the redux record useDropEvent reads, so
// without it every message resolves against an undefined drop and the hook returns nothing.
export const useDareRequirementMessages = ({ fallbackDrop } = {}) => {
    var _a;
    const { viewershipPlatforms, averageCcv = 0, averageTikTokViews = 0, averageYoutubeViews = 0, averageYoutubeShortsViews = 0, } = useUserCreatorData();
    const dropEvent = (_a = useDropEvent()) !== null && _a !== void 0 ? _a : fallbackDrop;
    const userCampaignData = useUserCampaignData(dropEvent === null || dropEvent === void 0 ? void 0 : dropEvent.mnemonicId);
    const { isBrand } = useIsBrand();
    const { isAdmin } = useIsAdmin();
    const isAuthenticated = useIsAuthenticated();
    const { viewershipPlatformsByDares } = useViewershipPlatformsByDares(undefined, fallbackDrop);
    const maxDaresByUserPlatforms = useMaxDaresByPlatforms({ fallbackDrop });
    if (!dropEvent) {
        return [];
    }
    if (!dropEvent.dares.length) {
        return [];
    }
    if (!isAuthenticated) {
        return ['Sign in to get started'];
    }
    if (isBrand || isAdmin) {
        return [];
    }
    if (!viewershipPlatforms.length) {
        return ['Connect a channel to get started.'];
    }
    if (maxDaresByUserPlatforms === 0) {
        const minimalRequirementsMessage = getMinimalRequirementsMessage({
            viewershipPlatforms: viewershipPlatformsByDares,
            dropEvent,
            averageCcv,
            averageTikTokViews,
            averageYoutubeShortsViews,
            averageYoutubeViews,
        });
        if (!minimalRequirementsMessage) {
            return [];
        }
        return [minimalRequirementsMessage];
    }
    return viewershipPlatformsByDares
        .map((platform) => {
        const maxDares = calculateMaxDaresByPlatform({
            averageCcv,
            averageYoutubeViews,
            averageYoutubeShortsViews,
            averageTikTokViews,
            platform,
            campaign: dropEvent,
        });
        const creatorAcceptedExtraDaresByPlatform = dropEvent === null || dropEvent === void 0 ? void 0 : dropEvent.creatorAcceptedExtraDaresByPlatform;
        return getDareMessageByPlatform({
            maxDares,
            dropEvent,
            viewershipPlatforms,
            platform,
            // Only a fallbackDrop caller reads the counts off the drop payload: it has no userCampaignData
            // record, while the payload carries the same numbers. Callers without one keep the original
            // behaviour of falling straight through to an empty record.
            creatorAcceptedDaresByPlatform: (userCampaignData === null || userCampaignData === void 0 ? void 0 : userCampaignData.creatorAcceptedDaresByPlatform)
                || (fallbackDrop && dropEvent.creatorAcceptedDaresByPlatform)
                || {},
            creatorAcceptedExtraDaresByPlatform,
        });
    })
        .filter(isString);
};
export function getDareMessageByPlatform({ platform, dropEvent, maxDares, viewershipPlatforms, creatorAcceptedDaresByPlatform, creatorAcceptedExtraDaresByPlatform, }) {
    var _a;
    if (!dropEvent) {
        return null;
    }
    const isBreakpoints = Boolean(dropEvent.viewershipBreakpoints);
    if (!viewershipPlatforms.includes(platform)) {
        return null;
    }
    if (platform === DarePlatform.Twitch && maxDares === Infinity) {
        return 'Based on your Twitch viewership, you can accept ALL Twitch dares.';
    }
    if (platform === DarePlatform.YouTube && maxDares === Infinity) {
        return 'Based on your YouTube viewership, you can accept ALL YouTube dares.';
    }
    if (platform === DarePlatform.YouTubeShorts && maxDares === Infinity) {
        return 'Based on your YouTube Videos viewership, you can accept ALL YouTube Videos dares.';
    }
    if (platform === DarePlatform.TikTok && maxDares === Infinity) {
        return 'Based on your TikTok viewership, you can accept ALL TikTok dares.';
    }
    if (!isBreakpoints) {
        return null;
    }
    if (maxDares === 0) {
        const requirementLabel = getMinRequirementLabel(platform, dropEvent.viewershipBreakpoints);
        if (requirementLabel) {
            return `You need at least ${requirementLabel} to accept ${getReadableViewershipPlatform(platform)} dares.`;
        }
    }
    const isAllDares = !Number.isFinite(maxDares);
    const acceptedDares = (_a = creatorAcceptedDaresByPlatform[platform]) !== null && _a !== void 0 ? _a : 0;
    const acceptedExtraDares = (creatorAcceptedExtraDaresByPlatform === null || creatorAcceptedExtraDaresByPlatform === void 0 ? void 0 : creatorAcceptedExtraDaresByPlatform[platform]) || 0;
    const left = Math.max(0, maxDares - (acceptedDares - acceptedExtraDares));
    return `Based on your ${getReadableViewershipPlatform(platform)} viewership, you can accept ${isAllDares ? 'ALL' : maxDares} ${getReadableViewershipPlatform(platform)} dares.${isAllDares ? '' : ` (${left} left)`}`;
}
export function getMinRequirementLabel(platform, viewershipBreakpoints) {
    if (platform === DarePlatform.Twitch) {
        const min = getMinBreakpoint(viewershipBreakpoints === null || viewershipBreakpoints === void 0 ? void 0 : viewershipBreakpoints.twitch, VIEWERSHIP_REQUIRED_OFFSET);
        return min ? `${formatNumberWithCommas(min)} Twitch CCV` : null;
    }
    if (platform === DarePlatform.YouTube) {
        const min = getMinBreakpoint(viewershipBreakpoints === null || viewershipBreakpoints === void 0 ? void 0 : viewershipBreakpoints.youtube, VIEWERSHIP_REQUIRED_OFFSET);
        return min ? `${formatNumberWithCommas(min)} Average YouTube Videos Views` : null;
    }
    if (platform === DarePlatform.YouTubeShorts) {
        const min = getMinBreakpoint(viewershipBreakpoints === null || viewershipBreakpoints === void 0 ? void 0 : viewershipBreakpoints.youtubeShorts, VIEWERSHIP_REQUIRED_OFFSET);
        return min ? `${formatNumberWithCommas(min)} Average YouTube Shorts Views` : null;
    }
    if (platform === DarePlatform.TikTok) {
        const min = getMinBreakpoint(viewershipBreakpoints === null || viewershipBreakpoints === void 0 ? void 0 : viewershipBreakpoints.tiktok, VIEWERSHIP_REQUIRED_OFFSET);
        return min ? `${formatNumberWithCommas(min)} Average TikTok Views` : null;
    }
    return null;
}
export function getMinimalRequirementsMessage({ dropEvent, viewershipPlatforms, averageCcv, averageYoutubeViews, averageYoutubeShortsViews, averageTikTokViews, }) {
    const { viewershipBreakpoints } = dropEvent;
    const viewershipByPlatform = {
        [DarePlatform.Twitch]: averageCcv || 0,
        [DarePlatform.YouTube]: averageYoutubeViews,
        [DarePlatform.YouTubeShorts]: averageYoutubeShortsViews,
        [DarePlatform.TikTok]: averageTikTokViews,
    };
    const userRequirementsMessages = viewershipPlatforms
        .map((platform) => {
        var _a;
        const label = getMinRequirementLabel(platform, viewershipBreakpoints);
        if (!label)
            return null;
        const minValue = getMinBreakpoint(viewershipBreakpoints === null || viewershipBreakpoints === void 0 ? void 0 : viewershipBreakpoints[platform], VIEWERSHIP_REQUIRED_OFFSET);
        if (minValue && minValue > ((_a = viewershipByPlatform[platform]) !== null && _a !== void 0 ? _a : 0)) {
            return label;
        }
        return null;
    })
        .filter(item => item);
    const canUserParticipate = userRequirementsMessages.length !== viewershipPlatforms.length;
    if (!canUserParticipate) {
        return `You need at least ${userRequirementsMessages.join(' or ')} to participate in this drop.`;
    }
    return null;
}
export function getMinBreakpoint(breakpoints, offset = 0) {
    const countBreakpoints = breakpoints === null || breakpoints === void 0 ? void 0 : breakpoints.map(({ viewershipCount }) => viewershipCount);
    if (!(countBreakpoints === null || countBreakpoints === void 0 ? void 0 : countBreakpoints.length)) {
        return null;
    }
    return Math.min(...countBreakpoints) + offset;
}
